import type {
  InboxItem,
  MatchData,
  AuditEntry,
  Member,
  MemberLite,
  Note,
  Opponent,
  OpponentSummary,
  PlayerFile,
  RosterEntry,
  RoundRow,
  Scouting,
} from '../types'
import type { Role, SessionInfo } from './session'

// The API is same-origin under /api (Vite proxies to Functions in dev; SWA/Front Door in prod).
const API = '/api'

/** The API holds the session (HttpOnly cookie) — the browser can't read it, so ask. */
export interface ParseStatus {
  state: 'QUEUED' | 'PARSING' | 'RETRYING' | 'PARSED' | 'FAILED'
  attempt?: number
  maxAttempts?: number
  error?: string
  at?: string
}

/** Parse progress for a demo. 404 = nothing written yet, treated as still queued. */
export async function fetchParseStatus(demoId: string): Promise<ParseStatus | null> {
  const r = await fetch(`${API}/matches/${demoId}/status`)
  if (!r.ok) return null
  return (await r.json()) as ParseStatus
}

export interface UnfiledDemo {
  demoId: string
  map?: string
  teams?: string[]
  state?: string
  error?: string
  addedAt?: string
}

export async function fetchUnfiled(): Promise<UnfiledDemo[]> {
  const r = await fetch(`${API}/scouting/unfiled`, { cache: 'no-store' })
  if (!r.ok) return []
  return ((await r.json()) as { demos: UnfiledDemo[] }).demos ?? []
}

export async function fetchSeason(league?: string | null, refresh = false): Promise<import('../types').Season> {
  const q = new URLSearchParams()
  if (league) q.set('league', league)
  // POST = rebuild. It writes a fresh AI self-review, and a GET that spends tokens is reachable by
  // clicking a link someone sent you.
  const r = await fetch(`${API}/season${q.toString() ? `?${q}` : ''}`, { method: refresh ? 'POST' : 'GET' })
  if (!r.ok) throw new Error('season_failed')
  return r.json()
}

export interface PreMatchBrief {
  matchId: string
  opponent: string
  scheduledAt: number
  competition: string
  /** Exactly what would be posted to Discord. */
  text: string
}

/** What the pre-match brief will say, without posting it. */
export async function fetchBriefs(): Promise<{ briefs: PreMatchBrief[]; discord: boolean }> {
  const r = await fetch(`${API}/prematch`)
  if (!r.ok) return { briefs: [], discord: false }
  return r.json()
}

/** Send one now (coach and up server-side). */
export async function sendBrief(matchId: string): Promise<boolean> {
  const r = await fetch(`${API}/prematch?matchId=${encodeURIComponent(matchId)}`, { method: 'POST' })
  if (!r.ok) return false
  return (((await r.json()) as { sent: string[] }).sent ?? []).includes(matchId)
}

export interface NextMatch {
  matchId: string
  scheduledAt: number
  competition: string
  opponent: string
  opponentTeamId: string
  opponentId?: string
  vetoReady?: boolean
  played?: boolean
  haveDemo?: boolean
  forfeit?: ForfeitResult
  /** Which platform the fixture came from. Absent means FACEIT — every row predates esplay, and
   *  the FACEIT-only affordances (forfeit marking, the demo-missing badge, veto by team id) key
   *  off this rather than trying to infer it. */
  source?: 'faceit' | 'esplay'
  /** Maps in the series. Nuke-liiga group games are bo1; ESEA is not. */
  bestOf?: number
  /** A played fixture's map score, ours first. esplay only — FACEIT results come from the demo. */
  series?: [number, number]
}

/** A fixture nobody played: forfeit win (they never joined) or forfeit loss (we didn't). */
export type ForfeitResult = 'ffw' | 'ffl'

/** Scheduled/ongoing league fixtures. prefetch opens the opponent's folder and warms their veto. */
export async function fetchUpcoming(prefetch = true): Promise<NextMatch[]> {
  const r = await fetch(`${API}/faceit/upcoming`, { method: prefetch ? 'POST' : 'GET' })
  if (!r.ok) return []
  return ((await r.json()) as { matches: NextMatch[] }).matches ?? []
}

/** Nuke-liiga fixtures from the esplay catalog, in the shared NextMatch shape so they sit in the
 *  same "Next up" list as the ESEA ones — a coach wants one list of what is next, not two.
 *
 *  Empty until the catalog has been built at least once (the daily timer, or a coach hitting
 *  POST /esplay/ingest). An empty list is a normal state here, not a failure.
 *
 *  esplay demos are deliberately absent: fetching one needs a signed-in esplay session, so these
 *  rows never carry `haveDemo` and must never be badged as owing a demo. */
export async function fetchEsplayFixtures(): Promise<NextMatch[]> {
  const r = await fetch(`${API}/esplay/fixtures`)
  if (!r.ok) return []
  const d = (await r.json()) as {
    league?: string
    fixtures?: {
      bracketId: string
      opponent: string
      opponentTeamId: string
      startsAt: number
      bestOf: number
      played: boolean
      series?: [number, number]
      /** The scouting folder we hold on them, joined server-side. Absent means we have no folder
       *  for that team — not that league fixtures can't be scouted. */
      opponentId?: string
      vetoReady?: boolean
    }[]
  }
  return (d.fixtures ?? []).map((f) => ({
    // Namespaced: a bracket id and a FACEIT match id share one key space in the merged list.
    matchId: `esplay:${f.bracketId}`,
    scheduledAt: f.startsAt,
    competition: d.league ?? 'Nuke-liiga',
    opponent: f.opponent,
    // Deliberately blank. Everywhere else in this type `opponentTeamId` is a FACEIT id, and an
    // esplay one here would send the veto sheet to FACEIT with an id it has never seen.
    opponentTeamId: '',
    opponentId: f.opponentId,
    vetoReady: f.vetoReady,
    played: f.played,
    source: 'esplay' as const,
    bestOf: f.bestOf,
    series: f.series,
  }))
}

// --- Scouting gaps: which of the next opponents' demos we are missing ---
export interface OpponentDemo {
  matchId: string
  teams: string[]
  scheduledAt: number
  maps: string[]
  /** FACEIT has published a demo for this match. When false there is nothing to fetch, by any
   *  means — it is not a to-do, and the UI must not present it as one. */
  published: boolean
  held: boolean
  roomUrl: string
  /** Which tournament the game was played in. A league opponent's rows are no longer all from one
   *  competition — their ESEA games sit beside their Nuke-liiga ones — so the row has to say. */
  competition?: string
}
export interface FixtureGap {
  matchId: string
  opponent: string
  scheduledAt: number
  competition: string
  opponentId?: string
  held: number
  available: number
  demos: OpponentDemo[]
}

/** What we're missing on the teams we play next. Read-only — it queues nothing and spends nothing,
 *  so it is safe to fetch on every page load. */
export async function fetchDemoGaps(): Promise<FixtureGap[]> {
  const r = await fetch(`${API}/scouting/missing`, { cache: 'no-store' })
  if (!r.ok) return []
  return ((await r.json()) as { fixtures: FixtureGap[] }).fixtures ?? []
}

/** Hand the parser a signed demo link to fetch itself, instead of downloading ~300 MB and
 *  uploading the same bytes back. Coach-gated server-side; the URL is host-restricted. */
export async function addDemoFromUrl(
  url: string,
  origin?: 'scouting',
): Promise<{ demoId: string; league: string | null }> {
  const r = await fetch(`${API}/demos/from-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, ...(origin ? { origin } : {}) }),
  })
  if (!r.ok) {
    const { error, detail } = (await r.json().catch(() => ({}))) as { error?: string; detail?: string }
    if (r.status === 403) throw new Error('this needs the coach role')
    throw new Error(detail ?? error ?? `from-url ${r.status}`)
  }
  return r.json()
}

/** Rebuild the Nuke-liiga catalog now. Coach and up.
 *
 *  Normally the nightly timer does this, but the catalog starts empty — so until it has run once,
 *  every esplay-backed thing in the portal (fixtures, the veto sheet for a Nuke opponent) is blank
 *  and looks broken rather than unseeded. This is the button that seeds it. */
export async function refreshEsplayCatalog(): Promise<{
  tournament: string
  fixtures: number
  played: number
  ours: number
  upcoming: number
}> {
  const r = await fetch(`${API}/esplay/ingest`, { method: 'POST' })
  if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? 'ingest_failed')
  return r.json()
}

/** Open a scouting folder for every other team in Nuke-liiga. Coach and up.
 *
 *  The esplay twin of `addLeagueOpponents`. Folders carry the season label and NO team id — an
 *  esplay id is not a FACEIT one — so their veto sheets resolve by name against the catalog. */
export async function addEsplayLeagueOpponents(): Promise<{
  league: string
  created: string[]
  skipped: string[]
}> {
  const r = await fetch(`${API}/esplay/league-opponents`, { method: 'POST' })
  if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? 'create_failed')
  return r.json()
}

/** Mark a fixture as a walkover, or clear it with null. Coach and up; 403 otherwise.
 *  Returns whether it stuck — the caller shows the mark optimistically and has to put it back. */
export async function setFixtureForfeit(matchId: string, result: ForfeitResult | null): Promise<boolean> {
  const r = await fetch(`${API}/faceit/fixtures/${encodeURIComponent(matchId)}/forfeit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ result }),
  })
  return r.ok
}

/** Map veto sheet. 409 = the folder has no FACEIT team linked, so there's nothing to build from. */
export async function fetchVeto(
  opponentId: string,
  refresh = false,
): Promise<import('../types').VetoSheet | { error: string }> {
  const r = await fetch(`${API}/opponents/${opponentId}/veto`, { method: refresh ? 'POST' : 'GET' })
  if (!r.ok) return { error: ((await r.json().catch(() => ({}))) as { error?: string }).error ?? 'veto_failed' }
  return r.json()
}

/** Who am I? The cookie is HttpOnly, so the browser can't read it — the API answers instead, with
 *  the role the roster gives us today rather than whatever was true when the cookie was minted. */
export async function fetchSession(): Promise<SessionInfo> {
  const offline: SessionInfo = { authed: false, user: null, methods: { steam: false } }
  const r = await fetch(`${API}/session`)
  if (!r.ok) return offline
  const j = (await r.json()) as Partial<SessionInfo>
  return { authed: !!j.authed, user: j.user ?? null, methods: j.methods ?? offline.methods }
}

/** Where the browser goes to sign in with Steam. A full navigation, not fetch: it's an OpenID
 *  redirect chain that ends back on our origin with a cookie set. */
export const steamLoginUrl = (): string => `${API}/auth/steam`

export async function signOut(): Promise<void> {
  await fetch(`${API}/session`, { method: 'DELETE' }).catch(() => {})
}

/** Roster administration (admin only — every route 403s otherwise). */
export async function fetchRosterMembers(): Promise<Member[]> {
  const r = await fetch(`${API}/roster`)
  if (!r.ok) throw new Error(r.status === 403 ? 'forbidden' : `roster ${r.status}`)
  return ((await r.json()) as { members: Member[] }).members ?? []
}

/** Which parser version produced each demo's numbers (admin only). */
export async function fetchEngines(): Promise<{ groups: { engine: string; count: number; demoIds: string[] }[]; current: string }> {
  const r = await fetch(`${API}/engines`)
  if (!r.ok) return { groups: [], current: 'unknown' }
  return r.json()
}

/** Who did what, newest first (admin only). */
export async function fetchAudit(): Promise<AuditEntry[]> {
  const r = await fetch(`${API}/audit`)
  if (!r.ok) return []
  return ((await r.json()) as { entries: AuditEntry[] }).entries ?? []
}

export async function saveRosterMember(steamid64: string, role: Role, nick?: string): Promise<Member> {
  const r = await fetch(`${API}/roster`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // nick omitted = leave it alone; '' = clear it back to the Steam persona.
    body: JSON.stringify(nick === undefined ? { steamid64, role } : { steamid64, role, nick }),
  })
  if (!r.ok) {
    const { error } = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(error ?? `roster ${r.status}`)
  }
  return r.json()
}

export async function deleteRosterMember(steamid64: string): Promise<void> {
  const r = await fetch(`${API}/roster/${steamid64}`, { method: 'DELETE' })
  if (!r.ok) {
    const { error } = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(error ?? `roster ${r.status}`)
  }
}

/** Sign one person out everywhere without touching their access — the "that laptop was stolen"
 *  lever. They can sign in again straight away on a machine they trust. */
export async function revokeMemberSessions(steamid64: string): Promise<void> {
  const r = await fetch(`${API}/roster/${steamid64}/revoke`, { method: 'POST' })
  if (!r.ok) {
    const { error } = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(error ?? `revoke ${r.status}`)
  }
}

// --- Review notes ---
export async function fetchNotes(demoId: string): Promise<Note[]> {
  const r = await fetch(`${API}/notes/${demoId}`)
  if (!r.ok) return []
  return ((await r.json()) as { notes: Note[] }).notes ?? []
}

/** Search notes across every match — "what did we say about their B split last time". */
export async function searchNotes(q: string): Promise<Note[]> {
  if (q.trim().length < 2) return []
  const r = await fetch(`${API}/notes?q=${encodeURIComponent(q)}`)
  if (!r.ok) return []
  return ((await r.json()) as { notes: Note[] }).notes ?? []
}

export async function postNote(
  demoId: string,
  note: { body: string; round?: number; mentions?: string[]; assignees?: string[]; staffOnly?: boolean },
): Promise<Note> {
  const r = await fetch(`${API}/notes/${demoId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(note),
  })
  if (!r.ok) {
    const { error } = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(error ?? `note ${r.status}`)
  }
  return r.json()
}

/** Edit a note, or tick an assignment off with {done}. */
export async function patchNote(
  demoId: string,
  id: string,
  patch: {
    body?: string
    round?: number | null
    mentions?: string[]
    assignees?: string[]
    done?: boolean
    /** Clear a mention from your own inbox. Assignments refuse this — mark them done instead. */
    dismiss?: boolean
  },
): Promise<Note> {
  const r = await fetch(`${API}/notes/${demoId}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!r.ok) throw new Error(`note ${r.status}`)
  return r.json()
}

export async function deleteNote(demoId: string, id: string): Promise<void> {
  const r = await fetch(`${API}/notes/${demoId}/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`note ${r.status}`)
}

/** What's aimed at me: assignments first, then mentions. */
export async function fetchInbox(): Promise<InboxItem[]> {
  const r = await fetch(`${API}/inbox`)
  if (!r.ok) return []
  return ((await r.json()) as { items: InboxItem[] }).items ?? []
}

/** The replayer's saved telestration for a match, by round index. */
export interface Drawings {
  byRound: Record<number, { color: string; pts: [number, number][] }[]>
  /** The demo was reparsed after these were drawn — the rounds may have moved under them. */
  stale?: boolean
}

export async function fetchDrawings(demoId: string): Promise<Drawings> {
  const r = await fetch(`${API}/drawings/${demoId}`)
  if (!r.ok) return { byRound: {} }
  const j = (await r.json()) as Partial<Drawings>
  return { byRound: j.byRound ?? {}, stale: !!j.stale }
}

/** Saving is coach-only server-side — a 403 here means "you drew, we just don't keep it". */
/** Returns what was actually stored — the server bounds the size, and the two disagreeing silently
 *  is how you end up showing strokes that aren't saved anywhere. */
export async function saveDrawings(
  demoId: string,
  byRound: Record<number, { color: string; pts: [number, number][] }[]>,
): Promise<Drawings['byRound']> {
  const r = await fetch(`${API}/drawings/${demoId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ byRound }),
  })
  if (!r.ok) throw new Error(`drawings ${r.status}`)
  return ((await r.json()) as { byRound: Drawings['byRound'] }).byRound ?? byRound
}

/** Who can be mentioned or assigned — everyone on the roster, not just those in demos. */
export async function fetchMembers(): Promise<MemberLite[]> {
  const r = await fetch(`${API}/members`)
  if (!r.ok) return []
  return ((await r.json()) as { members: MemberLite[] }).members ?? []
}

export interface MatchListItem {
  id: string // demoId, or 'sample'
  map: string
  rounds: number
  teamScore: Record<string, number>
  league?: string // e.g. "ESEA · S58 EU Intermediate A - Regular Season"
  source?: string
  finishedAt?: number
  sample?: boolean
}

/** Set/override a match's league tag (Nuke-liiga, scrims, or backfilling). */
export async function setMatchLeague(id: string, league: string, source?: string): Promise<void> {
  const r = await fetch(`${API}/matches/${id}/meta`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ league, ...(source ? { source } : {}) }),
  })
  if (!r.ok) throw new Error(`tag ${r.status}`)
}

/** List parsed matches from the API. */
export async function fetchMatches(): Promise<MatchListItem[]> {
  try {
    const r = await fetch(`${API}/matches`, { cache: 'no-store' })
    if (!r.ok) return []
    const { matches } = (await r.json()) as {
      matches: {
        demoId: string
        map: string
        rounds: number
        teamScore: Record<string, number>
        league?: string
        source?: string
        finishedAt?: number
      }[]
    }
    return matches
      .map((m) => ({
        id: m.demoId,
        map: m.map,
        rounds: m.rounds,
        teamScore: m.teamScore,
        league: m.league,
        source: m.source,
        finishedAt: m.finishedAt,
      }))
      // Newest first. The API returns blob-listing order, which is neither chronological nor
      // stable — the list read Jul 16, 14, 19, 19, 19. Sorted here so every consumer agrees.
      .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0))
  } catch {
    return []
  }
}

export async function fetchScoreboard(id: string): Promise<MatchData> {
  const r = await fetch(`${API}/matches/${id}/scoreboard`)
  if (!r.ok) throw new Error(`scoreboard ${r.status}`)
  return r.json()
}

export async function deleteMatch(id: string): Promise<void> {
  const r = await fetch(`${API}/matches/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`delete ${r.status}`)
}

/** `null` means the pipeline hasn't written this review yet (HTTP 202) — not an error, and not
 *  something the caller should fix by asking for one. A GET never spends tokens: the parse enqueues
 *  the review and it lands 20-40 s after the match appears, so "not yet" is a normal state for
 *  anyone who clicks through quickly. Poll; don't generate. */
export async function fetchInsights(
  id: string,
  refresh = false,
): Promise<import('../types').MatchInsights | null> {
  const r = await fetch(`${API}/insights/${id}`, { method: refresh ? 'POST' : 'GET' })
  if (r.status === 202) return null
  if (r.status === 501) throw new Error('AI review isn’t configured for this deployment yet.')
  if (r.status === 429) throw new Error(await budgetMessage(r))
  if (!r.ok) throw new Error(`insights ${r.status}`)
  return r.json()
}

/** The one AI failure that is a STATE rather than a fault, written so it reads as one.
 *
 *  Both AI endpoints answer a spent monthly token cap with 429 and
 *  `{error:'budget_exhausted', detail:'openai_budget_exhausted: <spent> of <cap> tokens used in
 *  <month>'}`. The page used to render that as `report 429` in red — a number nobody can act on,
 *  beside an empty report, with "regenerate" sitting there inviting another refused call. The
 *  numbers are already in the body; this says what they mean and what to do about it. */
async function budgetMessage(r: Response): Promise<string> {
  const detail = await r
    .json()
    .then((b: { detail?: string }) => b?.detail ?? '')
    .catch(() => '')
  const m = /([\d.]+) of ([\d.]+) (\S+)/.exec(detail)
  const spend = m ? ` — ${m[1]} of ${m[2]} ${m[3]} used.` : '.'
  return `This month’s AI budget is spent${spend} Nothing is broken and nothing else is affected — generation resumes when the counter resets on the 1st, or sooner if the monthly ceiling (AOAI_MONTHLY_TOKEN_CAP) is raised.`
}

// --- Opponent scouting ---
export async function fetchOpponents(): Promise<OpponentSummary[]> {
  const r = await fetch(`${API}/opponents`, { cache: 'no-store' })
  if (!r.ok) return []
  return ((await r.json()) as { opponents: OpponentSummary[] }).opponents ?? []
}
/** Create a folder. Pass a plain name, or a FACEIT team URL / id / exact name as `team` to resolve
 *  the real team (so the folder carries its faceitTeamId for roster lookups). */
export async function createOpponent(name: string, team?: string): Promise<Opponent> {
  const r = await fetch(`${API}/opponents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(team ? { team } : { name }),
  })
  if (r.status === 404) throw new Error('No FACEIT team found for that link/name.')
  if (!r.ok) throw new Error('create opponent failed')
  return r.json()
}
/** Create a folder for every other team in our current FACEIT division, stamped with the season. */
export async function addLeagueOpponents(): Promise<{ league: string; created: string[]; skipped: string[] }> {
  const r = await fetch(`${API}/faceit/league-opponents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
  if (r.status === 501) throw new Error('FACEIT isn’t configured for this deployment.')
  if (r.status === 409) throw new Error('No league found yet — the daily FACEIT pull hasn’t catalogued one.')
  if (!r.ok) throw new Error(`division ${r.status}`)
  return r.json()
}

/** End-of-season cleanup: drop every folder carrying this season label. */
/** Clear a whole season. The API removes as many folders as it can fit in one request and reports
 *  what is left, so this keeps calling until nothing is — one request for the whole season used to
 *  run past the 230s HTTP ceiling and be killed mid-folder. Returns the total removed. */
export async function removeLeagueOpponents(
  league: string,
  onProgress?: (removed: number) => void,
): Promise<number> {
  let total = 0
  for (;;) {
    const r = await fetch(`${API}/faceit/league-opponents?league=${encodeURIComponent(league)}`, {
      method: 'DELETE',
    })
    if (!r.ok) throw new Error(`remove ${r.status}`)
    const { removed, remaining } = (await r.json()) as { removed: number; remaining?: number }
    total += removed
    onProgress?.(total)
    // `!removed` guards the loop against a server that reports work left but can't make progress.
    if (!remaining || !removed) return total
  }
}

/** End-of-season: drop every scouting-uploaded demo, whatever folder holds it. Our own matches are
 *  not touched. Resumable like the per-season clear — keep calling until nothing is left. */
export async function clearScoutingDemos(
  onProgress?: (removed: number) => void,
): Promise<{ removed: number; detachedFrom: number }> {
  let removed = 0
  let detachedFrom = 0
  for (;;) {
    const r = await fetch(`${API}/scouting/demos`, { method: 'DELETE' })
    // Name the two that are about the caller rather than the server, since "clear 401" next to a
    // button that appears to do nothing is not a diagnosis.
    if (r.status === 401) throw new Error('not signed in any more — reload the page and sign in again')
    if (r.status === 403) throw new Error('this needs the coach role')
    if (!r.ok) throw new Error(`clear ${r.status}`)
    const j = (await r.json()) as { removed: number; remaining?: number; detachedFrom?: number }
    removed += j.removed
    detachedFrom = Math.max(detachedFrom, j.detachedFrom ?? 0)
    onProgress?.(removed)
    if (!j.remaining || !j.removed) return { removed, detachedFrom }
  }
}

/** Walk this team's roster history and return their games in EVERY tournament — the ESEA season a
 *  Nuke-liiga side also plays, a cup, another division. Minutes of FACEIT calls, so it is a button
 *  a coach presses rather than something a page does on load; the nightly ingest warms the teams we
 *  are about to play, and this is for the fixture that appeared today. */
export async function findTeamGames(faceitTeamId: string): Promise<import('../types').TeamGame[]> {
  const r = await fetch(`${API}/faceit/team-matches?teamId=${encodeURIComponent(faceitTeamId)}`, {
    method: 'POST',
  })
  if (!r.ok) throw new Error(r.status === 403 ? 'Coaches only.' : `Lookup failed (${r.status}).`)
  return ((await r.json()) as { matches: import('../types').TeamGame[] }).matches ?? []
}

export async function fetchOpponent(id: string): Promise<Opponent> {
  const r = await fetch(`${API}/opponents/${id}`, { cache: 'no-store' })
  if (!r.ok) throw new Error(`opponent ${r.status}`)
  return r.json()
}
export async function deleteOpponent(id: string): Promise<void> {
  await fetch(`${API}/opponents/${id}`, { method: 'DELETE' })
}
export async function addOpponentDemo(id: string, demoId: string, scoutedTeam: string): Promise<Opponent> {
  const r = await fetch(`${API}/opponents/${id}/demos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ demoId, scoutedTeam }),
  })
  if (!r.ok) throw new Error('add demo failed')
  return r.json()
}
/** File a parsed demo into every opponent folder it involves (both sides of a league match). */
export async function autoAssignMatch(
  demoId: string,
  create = false,
): Promise<{ opponent: string; team: string; created: boolean }[]> {
  const r = await fetch(`${API}/matches/${demoId}/auto-assign${create ? '?create=1' : ''}`, { method: 'POST' })
  if (!r.ok) return []
  return ((await r.json()) as { assigned: { opponent: string; team: string; created: boolean }[] }).assigned ?? []
}

export async function removeOpponentDemo(id: string, demoId: string): Promise<void> {
  await fetch(`${API}/opponents/${id}/demos/${demoId}`, { method: 'DELETE' })
}
export async function fetchScouting(id: string, refresh = false): Promise<Scouting> {
  const r = await fetch(`${API}/opponents/${id}/report`, { method: refresh ? 'POST' : 'GET' })
  if (r.status === 501) throw new Error('AI scouting isn’t configured for this deployment yet.')
  if (r.status === 429) throw new Error(await budgetMessage(r))
  if (!r.ok) throw new Error(`report ${r.status}`)
  return r.json()
}

export const replayUrl = (id: string) =>
  id === 'sample' ? '/replay.json' : `${API}/matches/${id}/replay`
/** One round's frames. Only meaningful when the index says `split` — demos parsed before 5 Aug
 *  carry their frames inline and have no such blob. The sample replay is a single bundled file and
 *  is never split. */
export const replayRoundUrl = (id: string, round: number) => `${API}/matches/${id}/replay-r${round}`
export const deepUrl = (id: string) =>
  id === 'sample' ? '/deepstats.json' : `${API}/matches/${id}/deep`

// --- Upload flow: POST /demos → PUT to SAS → POST complete ---
export async function uploadDemo(
  file: File,
  onProgress?: (pct: number) => void,
  origin?: 'scouting',
): Promise<{ demoId: string }> {
  const create = await fetch(`${API}/demos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name }),
  })
  if (!create.ok) throw new Error('create demo failed')
  const { demoId, blobPath, uploadUrl } = (await create.json()) as {
    demoId: string
    blobPath: string
    uploadUrl: string
  }

  await putWithProgress(uploadUrl, file, onProgress)

  const done = await fetch(`${API}/demos/${demoId}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // fileName lets the API pull the FACEIT match id out of it (1-<matchId>-1-1.dem.zst) and
    // auto-attach the league/date from the catalog.
    body: JSON.stringify({ blobPath, fileName: file.name, ...(origin ? { origin } : {}) }),
  })
  if (!done.ok) throw new Error('complete failed')
  return { demoId }
}

function putWithProgress(url: string, file: File, onProgress?: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(Math.round((100 * e.loaded) / e.total))
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload ${xhr.status}`)))
    xhr.onerror = () => reject(new Error('upload error'))
    xhr.send(file)
  })
}

/** Our roster with a form summary each. */
export async function fetchRoster(): Promise<RosterEntry[]> {
  const r = await fetch(`${API}/players`)
  if (!r.ok) throw new Error(`roster ${r.status}`)
  return ((await r.json()) as { players: RosterEntry[] }).players
}

/** One player: form over time, per-map splits, and every piece of feedback they've been given. */
export async function fetchPlayer(sid: string): Promise<PlayerFile> {
  const r = await fetch(`${API}/players/${sid}`)
  if (!r.ok) throw new Error(`player ${r.status}`)
  return (await r.json()) as PlayerFile
}

/** Round index for a scope: our season, or one opponent folder. */
export async function fetchRounds(scope: { kind: 'season' } | { kind: 'opponent'; id: string }): Promise<{ subject: string; rounds: RoundRow[] }> {
  const path = scope.kind === 'season' ? 'rounds/season' : `rounds/opponent/${scope.id}`
  const r = await fetch(`${API}/${path}`)
  if (!r.ok) throw new Error(`rounds ${r.status}`)
  return (await r.json()) as { subject: string; rounds: RoundRow[] }
}

// --- Saved round-finder situations (E4) ---
export interface Situation {
  id: string
  name: string
  query: Record<string, unknown>
  createdBy: string
  createdByName: string
  createdAt: string
}

export async function fetchSituations(): Promise<Situation[]> {
  const r = await fetch(`${API}/situations`)
  if (!r.ok) return []
  return ((await r.json()) as { items: Situation[] }).items ?? []
}

export async function saveSituation(name: string, query: Record<string, unknown>): Promise<Situation> {
  const r = await fetch(`${API}/situations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, query }),
  })
  if (!r.ok) {
    const { error } = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(error ?? `situations ${r.status}`)
  }
  return r.json()
}

export async function deleteSituation(id: string): Promise<void> {
  const r = await fetch(`${API}/situations/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`situations ${r.status}`)
}

/** What changed between two demos of the same opponent (F8). Oldest is resolved server-side, so
 *  "before → after" reads the way a human means it whichever order they were picked. */
export async function fetchDemoDiff(
  opponentId: string,
  a: string,
  b: string,
): Promise<import('../types').DemoDiff | { error: string }> {
  const r = await fetch(`${API}/opponents/${opponentId}/diff?a=${a}&b=${b}`)
  if (!r.ok) return { error: ((await r.json().catch(() => ({}))) as { error?: string }).error ?? 'diff_failed' }
  return r.json()
}

/** A session a coach can run, assembled from folds that already exist (F9). */
export async function fetchPracticePlan(): Promise<import('../types').PracticePlan> {
  const r = await fetch(`${API}/practice`)
  if (!r.ok) return { items: [], note: "Couldn't build a plan just now." }
  return r.json()
}

// --- Post-match debrief (F4) ---
export interface DebriefStep {
  id: 'review' | 'rounds' | 'assign' | 'notify'
  label: string
  done: boolean
  hint: string
}
export interface DebriefState {
  demoId: string
  reviewed?: boolean
  rounds: { round: number; why?: string }[]
  noteIds: string[]
  notifiedAt?: string
}
export interface DebriefView {
  debrief: DebriefState | null
  steps: DebriefStep[]
  next: string | null
}

export async function fetchDebrief(demoId: string): Promise<DebriefView | null> {
  const r = await fetch(`${API}/debrief/${demoId}`)
  if (!r.ok) return null
  return r.json()
}

export async function saveDebrief(demoId: string, patch: Partial<DebriefState>): Promise<DebriefView | null> {
  const r = await fetch(`${API}/debrief/${demoId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!r.ok) return null
  return r.json()
}

/** Posts the debrief to Discord. Returns the API's error key so the UI can say which thing failed
 *  — "no webhook configured" and "Discord refused it" need different answers. */
export async function notifyDebrief(demoId: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const r = await fetch(`${API}/debrief/${demoId}/notify`, { method: 'POST' })
  if (r.ok) return { ok: true }
  const { error } = (await r.json().catch(() => ({}))) as { error?: string }
  return { ok: false, error: error ?? `notify ${r.status}` }
}

// --- Playlists (F6) ---
export interface PlaylistEntry { demoId: string; round: number; why?: string }
export interface Playlist {
  id: string
  name: string
  entries: PlaylistEntry[]
  createdByName: string
  createdAt: string
  updatedAt?: string
}

export async function fetchPlaylists(): Promise<Playlist[]> {
  const r = await fetch(`${API}/playlists`)
  if (!r.ok) return []
  return ((await r.json()) as { items: Playlist[] }).items ?? []
}

export async function savePlaylist(p: { id?: string; name: string; entries: PlaylistEntry[] }): Promise<Playlist> {
  const r = await fetch(`${API}/playlists`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })
  if (!r.ok) {
    const { error } = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(error ?? `playlists ${r.status}`)
  }
  return r.json()
}

export async function deletePlaylist(id: string): Promise<void> {
  const r = await fetch(`${API}/playlists/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`playlists ${r.status}`)
}
